import type { ContextBlock, DraftRequest } from './types';
import { estimateTokens } from './token-estimate';

const CHARS_PER_TOKEN = 3.8; // matches estimateTokens' char-based term closely enough for a naive cut

/**
 * Naive baseline truncation for the benchmark comparison tool: no ranking, just keeps context
 * blocks in their existing order and cuts the last one that doesn't fully fit. System prompt,
 * history, and the user's message are never touched — only the open-files context (the same
 * material A3 would rank) is trimmed. This is the deliberate contrast to A3's relevance-ranked
 * selection, so the comparison isolates *how* context was chosen rather than how much there is.
 */
export function truncateContextToTokenBudget(draft: DraftRequest, targetTokens: number): DraftRequest {
  const fixedText = [draft.systemPrompt, draft.historySummary ?? '', ...draft.history.map((t) => t.content), draft.userMessage].join(
    '\n',
  );
  const budgetForContext = Math.max(0, targetTokens - estimateTokens(fixedText));
  let remainingChars = Math.floor(budgetForContext * CHARS_PER_TOKEN);

  const truncatedBlocks: ContextBlock[] = [];
  for (const block of draft.contextBlocks) {
    if (remainingChars <= 0) break;
    if (block.content.length <= remainingChars) {
      truncatedBlocks.push(block);
      remainingChars -= block.content.length;
    } else {
      truncatedBlocks.push({ ...block, content: block.content.slice(0, remainingChars) });
      remainingChars = 0;
    }
  }
  return { ...draft, contextBlocks: truncatedBlocks };
}
